/**
 * One planet's body: its DERIVED look, painted by its LEARNER STATE.
 *
 * The split is the whole design.  `planetLook` decides what a world IS —
 * class, colours, motif, adornment — from its ring and its name alone,
 * so it is the same world on every screen and in every session.  This
 * component decides how that world is PAINTED right now: a planet the
 * learner has never opened is drawn cold (a ghost of its motif inside a
 * drawn rim, so you can see what waits), a planet in progress is lit,
 * and a crossed one glows.
 *
 * EVERY world is a disc.  A molten one is the sky's only exception and
 * it is still a disc — a darkened crust and two glowing seams laid over
 * the same motif — so the field stays calm and a hot world reads as hot
 * rather than as a different kind of object.  Its seams show in the cold
 * state too, faintly, because the law above cuts both ways: a world must
 * be recognisably itself before it is opened.
 *
 * That is why nothing below reads the registry and nothing in
 * `planetLook` reads the log.
 */

import { Motif } from "./Motif";
import type { Palette } from "./palette";
import { type PlanetLook, planetLook } from "./planetLook";
import type { Unlit } from "./settings";
import type { PlanetReading } from "./types";

type Props = {
  planet: PlanetReading;
  /**
   * WHICH ORBIT OUT this world is drawn on — not the ring's authored
   * level.  The look is keyed on the slot so a ring's accent colour and
   * its worlds' hues come from the same anchor; see `planetLook`.
   */
  slot: number | null;
  /** Disc radius in viewBox units. */
  s: number;
  P: Palette;
  nss: Record<string, unknown>;
  /** Force the warm painting — used by the opened-planet hero. */
  forceWarm?: boolean;
  /**
   * How an untouched world is drawn: its rim drawn in ("solid"), or the
   * whole body stepped back ("veiled") once enough of the sky has
   * warmed for the untouched ones to read as the background.  See
   * `settings.unlitFor`.
   */
  unlit?: Unlit;
};

/** The veil: an untouched world at this opacity, so the walked ones carry the light. */
const VEIL = 0.58;

/**
 * A molten world's seams.
 *
 * Two fissures and the node where they meet, rotated by half the world's
 * own tilt so no two are identical and all are plainly the same kind of
 * thing.  Each is drawn twice — a wide soft stroke for the heat, a thin
 * bright one for the crack — and the heat stroke breathes on a slow
 * cycle staggered per world, so a molten sky glows rather than blinks.
 *
 * Drawn inside the disc's clip, over the crust and over the motif: the
 * seams are the top layer of an ordinary world, never a replacement for
 * one.
 */
function Seams({ look, s, mono, P }: { look: PlanetLook; s: number; mono: boolean; P: Palette }) {
  const w = Math.max(0.4, s * 0.085);
  const ember = mono ? P.dim : look.spot;
  const heat = mono ? P.dim : look.hi;
  // The fork is where the two cracks meet; both paths start from it.
  const fork: [number, number] = [-0.04 * s, 0.1 * s];
  const seam =
    `M ${-0.74 * s} ${0.22 * s} C ${-0.36 * s} ${0.02 * s} ${fork[0]} ${fork[1]} ${0.7 * s} ${-0.18 * s}`;
  const branch =
    `M ${fork[0]} ${fork[1]} C ${0.06 * s} ${0.34 * s} ${0.15 * s} ${0.46 * s} ${0.24 * s} ${0.74 * s}`;
  return (
    <g
      transform={`rotate(${(look.tilt / 2).toFixed(1)})`}
      style={mono ? undefined : { animation: `zember 5.4s ease-in-out ${((look.tilt + 40) / 20).toFixed(1)}s infinite` }}
    >
      {[seam, branch].map((d) => (
        <path
          key={`heat-${d}`} d={d} fill="none" stroke={heat} strokeOpacity={mono ? 0.16 : 0.34}
          strokeWidth={w * 2.7} strokeLinecap="round"
        />
      ))}
      {[seam, branch].map((d) => (
        <path
          key={`crack-${d}`} d={d} fill="none" stroke={ember} strokeOpacity={mono ? 0.5 : 0.95}
          strokeWidth={w} strokeLinecap="round"
        />
      ))}
      <circle cx={fork[0]} cy={fork[1]} r={w * 1.15} fill={ember} fillOpacity={mono ? 0.55 : 0.92} />
    </g>
  );
}

const opened = (planet: PlanetReading) => planet.state !== "COLD";
const started = (planet: PlanetReading) =>
  planet.state === "CLIMBING" || planet.state === "CROSSED" || planet.state === "GRANTED";

function Adornment({ planet, slot, s, P, nss }: Props) {
  const look = planetLook(planet.id, slot);
  const cold = !opened(planet);
  const o = cold ? 0.45 : 0.85;
  const stroke = cold ? P.dim : look.base;

  switch (look.adornment) {
    case "rings":
      return (
        <>
          {[1.3, 1.55, 1.8].map((k, i) => (
            <ellipse
              key={i} rx={k * s} ry={k * s * 0.3} transform={`rotate(${look.tilt})`} fill="none"
              stroke={cold ? P.dim : i === 1 ? look.lo : look.hi}
              strokeOpacity={o * (0.95 - i * 0.15)} strokeWidth={i === 1 ? 0.9 : 0.6} {...nss}
            />
          ))}
        </>
      );
    case "tilted-pair":
      return (
        <>
          {[look.tilt - 35, look.tilt + 35].map((rot, i) => (
            <ellipse
              key={i} rx={1.5 * s} ry={1.5 * s * 0.26} transform={`rotate(${rot})`} fill="none"
              stroke={cold ? P.dim : look.hi} strokeOpacity={o * 0.7} strokeWidth="0.6" {...nss}
            />
          ))}
        </>
      );
    case "moon": {
      const a = (look.tilt * Math.PI) / 180 - 0.7;
      return (
        <>
          <circle
            r={1.4 * s} fill="none" stroke={stroke} strokeOpacity={o * 0.6}
            strokeWidth="0.5" strokeDasharray="1 1.6" {...nss}
          />
          <circle
            cx={1.4 * s * Math.cos(a)} cy={1.4 * s * Math.sin(a)} r={0.22 * s}
            fill={cold ? P.dim : look.hi} fillOpacity={o}
          />
        </>
      );
    }
    case "rays":
      return (
        <>
          {[20, 140, 260].map((a, i) => {
            const r0 = ((a + look.tilt) * Math.PI) / 180;
            const r1 = ((a + look.tilt + 14 * (i - 1)) * Math.PI) / 180;
            return (
              <line
                key={i} x1={s * Math.cos(r0)} y1={s * Math.sin(r0)}
                x2={1.55 * s * Math.cos(r1)} y2={1.55 * s * Math.sin(r1)}
                stroke={cold ? P.dim : look.spot} strokeOpacity={o * 0.9} strokeWidth="0.65" {...nss}
              />
            );
          })}
        </>
      );
    case "halo":
      return (
        <circle
          r={1.34 * s} fill="none" stroke={stroke} strokeOpacity={o * 0.45}
          strokeWidth="0.5" strokeDasharray="0.6 2.2" {...nss}
        />
      );
    default:
      return null;
  }
}

export function PlanetBody(props: Props) {
  const { planet, s, P, nss, forceWarm, unlit = "solid" } = props;
  const look = planetLook(planet.id, props.slot);
  const cold = !forceWarm && !opened(planet);
  const warm = forceWarm || started(planet);
  const veil = cold && unlit === "veiled" ? VEIL : 1;
  const molten = look.planetClass === "molten";
  // Cooling dims a body the way the reference's retention did: a planet
  // whose weakest idea is slipping loses light without losing identity.
  // Four bands, because the server sends a word — the audience law keeps
  // grip's scalar off the wire, and a body only ever needed a band.
  const DIM: Record<string, number> = { FRESH: 1, WARM: 0.88, FADING: 0.72, COLD: 0.62 };
  const dimK = planet.heat === null ? 1 : DIM[planet.heat] ?? 1;
  const gradientId = `atlas-g-${planet.id.replace(/[^a-zA-Z0-9]/g, "_")}`;
  const clipId = `${gradientId}-c-${String(s).replace(".", "_")}`;

  const gradient = (
    <defs>
      <radialGradient id={gradientId} cx="36%" cy="32%" r="88%">
        <stop offset="0%" stopColor={look.hi} />
        <stop offset="52%" stopColor={look.base} />
        <stop offset="100%" stopColor={look.lo} />
      </radialGradient>
      <radialGradient id={`${gradientId}-limb`} cx="38%" cy="34%" r="78%">
        <stop offset="0%" stopColor="#000" stopOpacity="0" />
        <stop offset="72%" stopColor="#000" stopOpacity="0" />
        <stop offset="100%" stopColor="#000" stopOpacity="0.38" />
      </radialGradient>
    </defs>
  );

  return (
    <g opacity={veil}>
      {gradient}
      <Adornment {...props} />
      {cold || !warm ? (
        <g>
          {/* the haze it will resolve out of — its own colour, already showing */}
          <circle r={1.05 * s} fill={`url(#${gradientId})`} opacity={P.night ? 0.15 : 0.13} />
          <circle r={0.74 * s} fill={`url(#${gradientId})`} opacity={opened(planet) ? 0.3 : P.night ? 0.2 : 0.16} />
          {/* the rim of the unknown — drawn in; at the bench, the bench's dashed gold */}
          <circle
            r={0.82 * s} fill="none" stroke={opened(planet) ? P.gold : look.base}
            strokeWidth={opened(planet) ? 1.1 : 0.8} strokeOpacity={opened(planet) ? 0.85 : P.night ? 0.8 : 0.68}
            strokeDasharray={opened(planet) ? "2.4 1.4" : "none"} {...nss}
          />
          <clipPath id={`${clipId}c`}>
            <circle r={0.82 * s} />
          </clipPath>
          <g clipPath={`url(#${clipId}c)`}>
            <Motif motif={look.motif} look={look} s={0.82 * s} P={P} mono />
            {molten && <Seams look={look} s={0.82 * s} mono P={P} />}
          </g>
          {/* something is in there */}
          <circle r={0.17 * s} fill={molten ? look.spot : look.hi} fillOpacity="0.95" />
        </g>
      ) : (
        <g>
          <circle
            r={s} fill={`url(#${gradientId})`} opacity={dimK}
            style={P.night ? { filter: `drop-shadow(0 0 3.5px ${look.base}77)` } : undefined}
          />
          <clipPath id={clipId}>
            <circle r={s} />
          </clipPath>
          <g clipPath={`url(#${clipId})`}>
            {/* the crust: a molten world's motif sits UNDER it, which is
                what makes the seams the brightest thing on the body */}
            {molten && <circle r={s} fill={look.lo} fillOpacity="0.42" />}
            <Motif motif={look.motif} look={look} s={s} P={P} mono={false} />
            {molten && <Seams look={look} s={s} mono={false} P={P} />}
          </g>
          <circle r={s} fill={`url(#${gradientId}-limb)`} pointerEvents="none" />
          {/* A molten world glows from within, so it takes no sun-spot —
              the one place the specular would read as a second light. */}
          {!molten && (
            <circle cx={-0.32 * s} cy={-0.36 * s} r={0.2 * s} fill="#FFF" opacity="0.35" pointerEvents="none" />
          )}
        </g>
      )}
    </g>
  );
}
